import { sendCriteoEvent } from '@ma-js-common/criteo-helpers';
import axios from 'axios';
import $ from 'jquery';
import noUiSlider from 'nouislider';
import numeral from 'numeral';
import urlJoin from 'url-join';

import { initInlineAccordion } from '../../common/accordion';
import { fetchParcelGeom, fetchPlaceGeom, getSearchFormUrl } from '../../common/common.es6';
import {
    DVF_PATH,
    DVF_TRANSACTION_FEATURE_ID,
    MA_LAYER,
    PARCEL_SELECTED_LAYER_ID,
    PLACE_SELECTED_LAYER_ID,
} from '../../common/constants.es6';
import { computeCriteoAType, getRealtorIds } from '../../common/criteo-tagging';
import { isMobile } from '../../common/device';
import {
    addClass,
    dispatchEvent,
    hasClass,
    removeClass,
    toggleClass,
} from '../../common/domHelpers.es6';
import { initializeGeoSearchBoxes } from '../../common/form';
import Ga from '../../common/ga';
import LocalStorage from '../../common/localStorage';
import MapboxMap from '../../common/map/mapbox-map.es6';
import { DVFMapStyle, patchMapStyle } from '../../common/map/styles';
import Navigation from '../../common/navigation';
import HTTPService from '../../common/services/HTTPService.es6';
import { EstateType, thorFormatPlaceName, thorPushEvent } from '../../common/thor';
import { displayErrorToaster } from '../../common/toaster.es6';
import { addURLParams, hasValue } from '../../common/utils';
import Wa from '../../common/wa';
import dvfInfowindowTemplate from '../../templates/dvf-infowindow.html';
import dvfListItemsTemplate from '../../templates/dvf-list-items.html';
import dvfListTemplate from '../../templates/dvf-list.html';
import pastSalesReportPopoverTemplate from '../../templates/past-sales-report-popover.html';
import { MapHelpers } from '../../vectormap/helpers.es6';

/**
 * Mapbox Layer ID for DVF transactions
 * @type {String}
 */
const TRANSACTIONS_LAYER_ID = 'transactions';

/**
 * Zoom level for place type without viewBox or geolocation
 * @type {number}
 */
const ZOOM_WITHOUT_VIEW_BOX = 17;

/**
 * Number of milliseconds for geolocation request timeout
 * @type {number}
 */
const GEOLOCATION_TIMEOUT = 5000;

/**
 * min value of price filter
 * @type {number}
 */
const MIN_PRICE = 0;

/**
 * max value of price filter
 * @type {number}
 */
const MAX_PRICE = 2000000;

/**
 * min value of area filter
 * @type {number}
 */
const MIN_AREA = 0;

/**
 * max value of area filter
 * @type {number}
 */
const MAX_AREA = 300;

/**
 * local storage key value for the onboarding tutorial
 * @type {string}
 */
const LOCAL_STORAGE_ONBOARDING_KEY = 'dvfOnboarding';

/**
 * File upload limit
 * @type {number}
 */
const REPORT_FILE_UPLOAD_LIMIT = 8388608; // 8MB

/**
 * Options for the selected Parcel layer
 * @type {Object}
 */
const PARCEL_LAYER_OPTIONS = {
    type: 'line',
    paint: {
        'line-width': 5,
        'line-color': '#1C90FF',
        'line-opacity': 1,
    },
    before: MA_LAYER,
};

/**
 * Options for the selected Place layer
 * @type {Object}
 */
const PLACE_LAYER_OPTIONS = {
    type: 'line',
    paint: {
        'line-width': 3,
        'line-color': '#1C90FF',
        'line-opacity': 1,
    },
};

/**
 * Filter form element (different on mobile or desktop)
 * @type {Element}
 */
let filterForm;

/**
 * instance of area slider
 */
let areaSlider;

/**
 * instance of price slider
 */
let priceSlider;

/**
 * Price range for slider to control slider handles behavior
 * @type {object}
 */
const PRICE_SLIDER_RANGE = {
    min: [MIN_PRICE, 25000],
    '40%': [300000, 50000],
    '65%': [700000, 100000],
    '70%': [1000000, 200000],
    max: [MAX_PRICE],
};

/**
 * Area range for slider to control slider handles behavior
 */
const AREA_SLIDER_RANGE = {
    min: [MIN_AREA, 10],
    '20%': [10, 10],
    '60%': [100, 10],
    '80%': [200, 10],
    max: [MAX_AREA, 10],
};

/**
 * Past sales report popover display state
 * @type {Boolean}
 */
let userDisablePastSalesReportPopover = false;

/**
 * Selector for realtor Item in the list
 * @type {string}
 */
const realtorItemSelector = '[data-reference-realtor]';

/**
 * Handle display of past sales report popover
 * @type {Function}
 */
function loadPastSalesReportPopover() {
    if (isMobile() || userDisablePastSalesReportPopover) {
        return;
    }

    window.setTimeout(() => {
        const popover = document.querySelector('[data-past-sales-report-popover-placeholder]');

        if (popover.innerHTML === '') {
            popover.innerHTML = pastSalesReportPopoverTemplate();

            document
                .querySelector('[data-past-sales-report-popover-close]')
                .addEventListener('click', e => {
                    e.preventDefault();
                    userDisablePastSalesReportPopover = true;
                    popover.innerHTML = '';
                });
        }
    }, 10 * 1000);
}

/**
 * Toggle a loader visibility in front of a pending content
 *
 * @param {String} contentDOMSelector selector of the content container in the DOM
 * @param {String} loaderDOMSelector selector of the loader container in the DOM
 * @param {Boolean} force whether you want the loader to appear or not
 */
function toggleLoaderState(contentDOMSelector, loaderDOMSelector, force) {
    const contentElement = document.querySelector(contentDOMSelector);
    const loaderElement = document.querySelector(loaderDOMSelector);

    toggleClass(contentElement, 'hide', hasValue(force) ? force : undefined);
    toggleClass(loaderElement, 'hide', hasValue(force) ? !force : undefined);
}

/**
 * Toggle the transaction list loader visibility
 *
 * @param {Boolean} force whether you want the loader to appear or not
 */
function toggleTransactionListLoaderState(force) {
    document.querySelector('[data-dvf-list]').scrollTop = 0;
    toggleLoaderState('[data-dvf-list-content]', '[data-dvf-list-loader]', force);
}

/**
 * Initialize realtor rec list click handler
 */
function initClickHandlerRealtorListItem() {
    const realtorListContainer = document.querySelector('[data-realtors-recommendation-container]');

    realtorListContainer.addEventListener('click', e => {
        const realtorUrl = e.target && e.target.dataset && e.target.dataset.referenceRealtorHref;
        if (realtorUrl) {
            window.open(realtorUrl, '_blank');
        }
    });
}

/**
 * Insert realtor recommendation in the DOM
 * @param {Promise} fetchRealtorsPromise promise to fetch realtors recommendation
 */
function insertRealtorsRecommendation(fetchRealtorsPromise) {
    const realtorListLoaderSelector = '[data-realtors-list-loader]';
    const realtorListContainerSelector = '[data-realtors-list-content]';
    const realtorListContainer = document.querySelector(realtorListContainerSelector);

    toggleLoaderState(realtorListContainerSelector, realtorListLoaderSelector, true);

    fetchRealtorsPromise
        .then(res => {
            realtorListContainer.innerHTML = res.data;
        })
        .catch(err => {
            global.console.log(err);
        })
        .finally(() => {
            toggleLoaderState(realtorListContainerSelector, realtorListLoaderSelector, false);
            Wa.setDisplayTracking($(realtorListContainerSelector));
            sendCriteoEvent({
                event: 'viewList',
                item: getRealtorIds(realtorItemSelector),
                atype: computeCriteoAType(realtorItemSelector),
            });
        });
}

/**
 * Reset report form values and error messages
 */
function resetReportForm() {
    const reportForm = document.querySelector('[data-report-form]');
    const inputErrorMessages = document.querySelectorAll('[data-report-input-error-msg]');

    // remove all error messages
    if (inputErrorMessages.length > 0) {
        inputErrorMessages.forEach(msg => {
            if (!hasClass(msg, 'hide')) {
                addClass(msg, 'hide');
            }
        });
    }

    // reset form values to default
    if (reportForm) {
        reportForm.reset();
    }
}

/**
 * Hide the form and display the content of the detail popin
 */
function hideReportForm() {
    // hide report modal
    addClass(document.querySelector('[data-report-success-wrapper]'), 'hide');
    removeClass(document.querySelector('[data-report-form-wrapper]'), 'hide');
    addClass(document.querySelector('[data-report-modal]'), 'hide');
    removeClass(document.querySelector('[data-dvf-modal-content]'), 'hide');

    // scroll to top when leave the report form
    document.querySelector('[data-dvf-modal-content]').scrollIntoView();

    // reset form to clean up errors
    resetReportForm();
}

/**
 * Validate reportForm
 * @return {boolean} true if there are errors and false if there are no error
 */
function validateReportForm() {
    const inputFiles = document.querySelectorAll('[data-file-input]');
    const inputFirstName = document.querySelector('[data-input-firstname]');
    const inputLastName = document.querySelector('[data-input-lastname]');
    const inputEmail = document.querySelector('[data-input-email]');
    const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/gim;

    let hasErrors = false;

    if (inputFirstName.value === '') {
        inputFirstName.nextElementSibling.innerText = 'Veuillez entrer votre prénom';
        removeClass(inputFirstName.nextElementSibling, 'hide');
        hasErrors = true;
    } else {
        addClass(inputFirstName.nextElementSibling, 'hide');
    }

    if (inputLastName.value === '') {
        inputLastName.nextElementSibling.innerText = 'Veuillez entrer votre nom';
        removeClass(inputLastName.nextElementSibling, 'hide');
        hasErrors = true;
    } else {
        addClass(inputLastName.nextElementSibling, 'hide');
    }

    if (!emailRegex.test(inputEmail.value)) {
        inputEmail.nextElementSibling.innerText = 'Veuillez entrer un email valide';
        removeClass(inputEmail.nextElementSibling, 'hide');
        hasErrors = true;
    } else {
        addClass(inputEmail.nextElementSibling, 'hide');
    }

    inputFiles.forEach(input => {
        if (!input.files[0]) {
            // eslint-disable-next-line no-param-reassign
            input.nextElementSibling.innerText = ' Veuillez soumettre un fichier';
            removeClass(input.nextElementSibling, 'hide');
            hasErrors = true;
        } else if (input.files[0].size > REPORT_FILE_UPLOAD_LIMIT) {
            // eslint-disable-next-line no-param-reassign
            input.nextElementSibling.innerText =
                ' Le fichier dépasse 8Mo, veuillez soumettre un fichier plus léger';
            removeClass(input.nextElementSibling, 'hide');
            hasErrors = true;
        } else {
            addClass(input.nextElementSibling, 'hide');
        }
    });

    return hasErrors;
}

/**
 * Handle report form submission
 * @param {number} featureId feature id of transaction
 */
function handleReportFormSubmit(featureId) {
    const reportForm = document.querySelector('[data-report-form]');
    const inputFirstName = document.querySelector('[data-input-firstname]');
    const inputLastName = document.querySelector('[data-input-lastname]');
    const inputEmail = document.querySelector('[data-input-email]');
    const inputProofOfIdentityFile = document.querySelector('[data-proof-of-identity-file-input]');
    const inputProofOfOwnershipFile = document.querySelector(
        '[data-proof-of-ownership-file-input]',
    );
    const submitBtn = document.querySelector('[data-report-form-submit-btn]');

    reportForm.addEventListener('submit', e => {
        e.preventDefault();

        // form validation
        const hasErrors = validateReportForm();

        if (!hasErrors) {
            const formData = new FormData();
            formData.append('firstname', inputFirstName.value);
            formData.append('lastname', inputLastName.value);
            formData.append('email', inputEmail.value);
            formData.append('proof_of_identity', inputProofOfIdentityFile.files[0]);
            formData.append('proof_of_ownership', inputProofOfOwnershipFile.files[0]);

            axios
                .post(`/signaler/transaction-${featureId}/`, formData, {
                    onUploadProgress: () => {
                        submitBtn.classList.add('pending', 'disabled');
                    },
                })
                .then(() => {
                    submitBtn.classList.remove('pending', 'disabled');
                    addClass(document.querySelector('[data-report-form-wrapper]'), 'hide');
                    removeClass(document.querySelector('[data-report-success-wrapper]'), 'hide');
                })
                .catch(err => {
                    window.console.error('error while submitting form', err);
                });
        }
    });
}

/**
 * hide the content of the detail popin and display the report form
 * @param {number} featureId transaction id to report
 */
function displayReportForm(featureId) {
    const reportModal = document.querySelector('[data-report-modal]');

    addClass(document.querySelector('[data-dvf-modal-content]'), 'hide');
    removeClass(reportModal, 'hide');

    // scroll to top for report modal
    reportModal.scrollIntoView();

    // set report form submit handler
    handleReportFormSubmit(featureId);

    // handle report form go back action
    document.querySelectorAll('[data-cancel-report-btn]').forEach(cancelButton => {
        cancelButton.addEventListener('click', hideReportForm);
    });
}

/**
 * Open transaction detail popin
 * @param {number} featureId feature id to open corresponding popin
 */
function openTransactionDetailPopin(featureId) {
    // start promise fetching realtor recommendation
    const getRealtorsPromise = HTTPService.get(
        addURLParams(`${DVF_PATH}/realtors/${featureId}`, {
            ga_category: 'DVF',
            ga_label_prefix: 'dvf_detail:realtors_list',
            wa_source: 'dvf',
            wa_name_prefix: 'dvf:realtors_list',
        }),
    );

    $.magnificPopup.open(
        {
            type: 'ajax',
            fixedContentPos: 'fixed',
            items: {
                src: `${DVF_PATH}/detail/${featureId}`,
            },
            callbacks: {
                ajaxContentAdded() {
                    Navigation.disablePageScroll();
                    // use event delegation to initialize click handler for realtor list items
                    initClickHandlerRealtorListItem();

                    initInlineAccordion(
                        '.realtor-dropdown',
                        '.realtor-dropdown__header',
                        '.realtor-dropdown__content',
                    );

                    insertRealtorsRecommendation(getRealtorsPromise);

                    document.querySelector('[data-report-button]').addEventListener('click', () => {
                        displayReportForm(featureId);
                    });
                },
                close() {
                    Navigation.enablePageScroll();
                    resetReportForm();
                },
            },
        },
        0,
    );
}

/**
 * Bind transaction detail popin opening to click event
 * @param {Element} wrapper element wrapping the detail popin
 */
function bindTransactionDetailsPopin(wrapper) {
    $(wrapper).on('click', '[data-dvf-transaction-id]', event => {
        const featureId = parseInt(event.currentTarget.getAttribute('data-dvf-transaction-id'), 10);
        openTransactionDetailPopin(featureId);
    });
}

/**
 * Memory cache to store features that should be displayed but are not provided by the API due to LIMIT
 * @type{Map[String]Object}
 */
const featuresMemoryCache = new Map();

/**
 * Last registered map zoom after a user interaction
 * @type {number}
 */
let currentMapZoom;

/**
 * Bind click event on toggle mobile view button
 */
function bindToggleMobileViewButton() {
    if (!isMobile()) {
        return;
    }

    function onToggleMobileViewButtonClick() {
        document
            .querySelectorAll('[data-dvf-list],[data-dvf-map]')
            .forEach(element => toggleClass(element, 'hide-sm'));
    }

    document.querySelectorAll('[data-dvf-toggle-mobile-view]').forEach(button => {
        button.addEventListener('click', onToggleMobileViewButtonClick);
    });
}

/**
 * Add missing transactions stored in the frontend
 *
 * @param {[]GeoJSON} geoJSON
 * @returns {[]GeoJSON} patchedGeoJSON
 */
function addFrontendMemoryToGeoJSON(geoJSON) {
    const newZoom = MA.mapboxMap.map.getZoom();
    const bounds = MA.mapboxMap.map.getBounds();
    let patchedGeoJSON = geoJSON;

    if (currentMapZoom === null || newZoom < currentMapZoom) {
        // zooming out or going elsewhere, we flush the memory cache
        featuresMemoryCache.clear();
    } else {
        // zooming in or panning, we try to use memory cache

        // cleaning features not anymore in the bounds
        featuresMemoryCache.forEach((value, key, map) => {
            const coordinates = {
                lng: value.geometry.coordinates[0],
                lat: value.geometry.coordinates[1],
            };
            if (!MapHelpers.isCoordinatesInBounds(coordinates, bounds)) {
                map.delete(key);
            }
        });

        // adding newly fetched features in the memory cache
        if (geoJSON.features) {
            geoJSON.features.forEach(feature => {
                const key = `${feature.properties.id}`;
                if (!featuresMemoryCache.has(key)) {
                    featuresMemoryCache.set(key, feature);
                }
            });
        }

        patchedGeoJSON = {
            ...geoJSON,
            features: Array.from(featuresMemoryCache.values()),
        };
    }

    currentMapZoom = newZoom;
    return patchedGeoJSON;
}

/**
 * Bind all events of the transaction list
 *
 * @param {Element} listContainer
 */
function bindTransactionsListEvents(listContainer) {
    if (listContainer.hasAttribute('data-dvf-list-content-bind')) {
        return;
    }

    bindTransactionDetailsPopin(listContainer);

    if (!isMobile()) {
        // Desktop only events (mouseover and mouseout)
        $(listContainer).on('mouseover', '[data-dvf-transaction-id]', event => {
            const featureId = parseInt(
                event.currentTarget.getAttribute('data-dvf-transaction-id'),
                10,
            );
            MA.mapboxMap.map.setFilter(`${TRANSACTIONS_LAYER_ID}-hover`, ['==', 'id', featureId]);
        });

        $(listContainer).on('mouseout', '[data-dvf-transaction-id]', () => {
            MA.mapboxMap.map.setFilter(`${TRANSACTIONS_LAYER_ID}-hover`, ['==', 'id', '']);
        });
    }

    listContainer.setAttribute('data-dvf-list-content-bind', true);
}

/**
 * Properly format roomCount field
 *
 * @param {Number} roomCount
 * @returns {String|undefined}
 */
function formatTransactionRoomCount(roomCount) {
    if (!roomCount) {
        return undefined;
    }

    return roomCount > 1 ? `${roomCount} pièces` : 'Studio';
}

/**
 * Update the transactions list in the DOM with fetched search
 *
 * @param {Object} geoJSON
 */
function updateTransactionsList(geoJSON) {
    const listContainer = document.querySelector('[data-dvf-list-content]');

    if (listContainer && geoJSON.features) {
        const transactions = geoJSON.features.map(({ properties }) => ({
            ...properties,
            area: properties.area ? `${properties.area} m²` : undefined,
            room_count: formatTransactionRoomCount(properties.room_count),
        }));

        const numberOfTransactions = transactions.length;
        const firstThreeTransactions = transactions.slice(0, 3);
        const fourthAndFollowingTransactions = transactions.slice(3, numberOfTransactions);

        listContainer.innerHTML = dvfListTemplate({
            numberOfTransactions,
            firstThreeTransactions: dvfListItemsTemplate({
                transactions: firstThreeTransactions,
            }),
            fourthAndFollowingTransactions: dvfListItemsTemplate({
                transactions: fourthAndFollowingTransactions,
            }),
        });

        toggleTransactionListLoaderState(false);
        bindTransactionsListEvents(listContainer);
    }
}

/**
 * Initialise thor tracking
 * @param {Object} geoJSON
 */
function initializeTracking(geoJSON) {
    const $transaction = $('[data-dvf-transaction]');

    $transaction.on('click', event => {
        thorPushEvent('select_item', {
            content_type: 'classified',
            item_id: event.currentTarget.dataset.dvfTransactionId,
            av_distribution_type_global: '2',
            av_items: geoJSON.features.map(item => {
                const {
                    type,
                    id,
                    price,
                    item_type: itemType,
                    zip,
                    city_name: cityName,
                } = item.properties;
                return {
                    list_name: type,
                    id,
                    price: price.replace(/[\s€]/g, ''),
                    estate_type: EstateType[itemType.key],
                    distribution_type: '2',
                    publication_id: 'MA',
                    name: type,
                    zip_code: zip,
                    country: 'france',
                    city: thorFormatPlaceName(
                        cityName.charAt(0).toUpperCase() + cityName.slice(1).toLowerCase(),
                    ),
                };
            }),
        });
    });
}

/**
 * Register the transactions layer for displaying blue dots on the map
 *
 * @param {MapboxMap} mapboxMap
 *
 * @return Promise
 */
function registerTransactionsLayer(mapboxMap) {
    return mapboxMap.registerFeatures(
        TRANSACTIONS_LAYER_ID,
        `${DVF_PATH}/search`,
        {},
        {
            updateOnBoundsChanged: true,
            withCache: false,
            iconColor: 'small-blue',
            hoverIconColor: 'big-blue',
            geoJSONFetchCallback: geoJSON => {
                updateTransactionsList(geoJSON);
                initializeTracking(geoJSON);
                const patchedGeoJSON = addFrontendMemoryToGeoJSON(geoJSON);

                if (patchedGeoJSON.features) {
                    // add type property for InfoWindow fetch specificity
                    patchedGeoJSON.features = patchedGeoJSON.features.map(feature => ({
                        ...feature,
                        properties: {
                            ...feature.properties,
                            type: DVF_TRANSACTION_FEATURE_ID,
                        },
                    }));
                }

                return patchedGeoJSON;
            },
            onBoundsChangedCallback: () => toggleTransactionListLoaderState(true),
        },
    );
}

/**
 * Register price labels layer for displaying prices underneath blue dots on the map
 *
 * @param {MapboxGL.Map} map
 */
function registerPriceLabelsLayer(map) {
    map.addLayer(
        {
            id: `${TRANSACTIONS_LAYER_ID}-prices`,
            type: 'symbol',
            source: TRANSACTIONS_LAYER_ID,
            layout: {
                'icon-image': 'white-rectangle',
                'text-field': ['get', 'price_short'],
                'text-anchor': 'top',
                'text-offset': [0, 1],
                'icon-text-fit': 'both',
                'icon-text-fit-padding': [5, 7, 3, 7],
                'text-size': 13,
                'text-line-height': 1,
                'text-font': ['Roboto Regular'],
            },
            paint: {
                'text-color': '#484848',
            },
        },
        `${TRANSACTIONS_LAYER_ID}-hover`,
    );
}

/**
 * Register or update (when already registered) Parcel and Place selected layers
 *
 * @param {Object} place
 */
function registerSelectedLayers(place) {
    const { mapboxMap } = MA;

    mapboxMap.registerGeoJSON(
        PARCEL_SELECTED_LAYER_ID,
        MapHelpers.getFeatureCollectionFromGeometry(null),
        PARCEL_LAYER_OPTIONS,
    );

    mapboxMap.registerGeoJSON(
        PLACE_SELECTED_LAYER_ID,
        MapHelpers.getFeatureCollectionFromGeometry(null),
        PLACE_LAYER_OPTIONS,
    );

    if (place.geotype_label === 'address') {
        fetchParcelGeom(place)
            .catch(global.console.error)
            .then(geometry =>
                mapboxMap.registerGeoJSON(
                    PARCEL_SELECTED_LAYER_ID,
                    MapHelpers.getFeatureCollectionFromGeometry(geometry),
                    PARCEL_LAYER_OPTIONS,
                ),
            );
    }

    fetchPlaceGeom(
        ['address', 'street', 'borough'].includes(place.geotype_label)
            ? { id: place.city_id, geotype_label: 'city' }
            : place,
    )
        .catch(global.console.error)
        .then(geometry => {
            mapboxMap.registerGeoJSON(
                PLACE_SELECTED_LAYER_ID,
                MapHelpers.getFeatureCollectionFromGeometry(geometry),
                PLACE_LAYER_OPTIONS,
            );
        });
}

/**
 * Call actions that must run when map is loaded
 *
 * @param {MapboxGl.map} map
 * @param {MapboxMap} mapboxMap
 * @param {Function} resolve
 */
function onMapLoaded(map, mapboxMap, resolve) {
    registerSelectedLayers(MA.Context.place);
    registerTransactionsLayer(mapboxMap)
        .then(() => {
            bindTransactionDetailsPopin('[data-vector-map]');
            registerPriceLabelsLayer(map);
        })
        .catch(error => {
            displayErrorToaster("Impossible d'afficher les transactions dans cette zone.");
            global.console.error(error);
        })
        .finally(() => {
            currentMapZoom = MA.mapboxMap.map.getZoom();
            resolve();
        });
}

/**
 * Initialize the DVF transactions map
 */
function initMap() {
    const mapWrapper = document.querySelector('[data-vector-map]');

    const mapOptions = {
        showControls: !isMobile(),
        maxZoom: 18,
        maxBounds: MapHelpers.boundsFromBox(
            // Large France viewbox
            'BOX(-9.554310372066936 40.65102942244246,16.59123676020627 51.97072067325661)',
        ),
        style: patchMapStyle(DVFMapStyle),
        initialInfoWindowTemplate: dvfInfowindowTemplate,
        onLoad: onMapLoaded,
    };

    if (MA.Context.place) {
        const { place } = MA.Context;

        if (place.coordinates && ['street', 'address'].includes(place.geotype_label)) {
            mapOptions.center = [place.coordinates.lng, place.coordinates.lat];
            mapOptions.zoom = ZOOM_WITHOUT_VIEW_BOX;
        } else {
            const boundsBox = place.viewport ? place.viewport : place.viewbox;
            mapOptions.bounds = MapHelpers.boundsFromBox(boundsBox);
        }
    }

    MA.mapboxMap = new MapboxMap(mapWrapper, 'mapbox-map', mapOptions);
}

/**
 *
 * @param {JQuery} $geoSearchBoxes
 * @param {JQuery} $geoSearchBox
 * @param {*} event
 * @param {Object} place
 */
function GeoSearchBoxesSuccessCallback($geoSearchBoxes, $geoSearchBox, event, selectedPlace) {
    const {
        id,
        geotype_label: geoTypeLabel,
        _geoloc: { lat, lng },
    } = selectedPlace;
    HTTPService.getGeo(`${MA.Config.PLACE_TYPE_PLURALS[geoTypeLabel]}/${id}`)
        .then(response => {
            toggleTransactionListLoaderState(true);

            const { place } = response.data;
            if (geoTypeLabel === 'address' || geoTypeLabel === 'street') {
                MA.mapboxMap.map.flyTo({ center: [lng, lat], zoom: ZOOM_WITHOUT_VIEW_BOX });
            } else {
                MA.mapboxMap.map.fitBounds(MapHelpers.boundsFromBox(place.viewport));
            }

            registerSelectedLayers(place);

            getSearchFormUrl(place)
                .then(url => {
                    const newUrl = urlJoin('/', $geoSearchBox.data('base-url'), url, '/');
                    if (!global.history.state || newUrl !== global.history.state.url) {
                        global.history.pushState(
                            {
                                url: newUrl,
                            },
                            'place',
                            newUrl,
                        );
                        dispatchEvent('pushstate');
                    }
                })
                .catch(global.console.error);
        })
        .catch(error => {
            displayErrorToaster('Une erreur est survenue.');
            global.console.error(error);
        });

    $geoSearchBoxes.each(function geoSearchBoxUrlEach() {
        $(this)
            .find('[data-geo-search-field]')
            .trigger('blur');
    });
}

/**
 * Check whether user has already used the explorer
 *
 * @returns {Boolean} true if has visited and false if never visited before
 */
function checkIfUserVisitedBefore() {
    return Boolean(LocalStorage.getItem(LOCAL_STORAGE_ONBOARDING_KEY));
}

/**
 * Set local storage flag when onboarding has started
 *
 */
function setOnboardingLocalStorageFlag() {
    LocalStorage.setItem(LOCAL_STORAGE_ONBOARDING_KEY, 'onboardingExplorerDone');
}

/**
 * Set onboarding tagging on leave action
 * @param {string} currentSlide
 */
function setOnLeaveOnboardingTagging(currentSlide) {
    Ga.send({
        hitType: 'event',
        eventCategory: 'DVF',
        eventAction: 'dvf_explorer:landed',
        eventLabel: `from:dvf_explorer:onbarding:popin:${currentSlide}`,
    });
}

/**
 * Set onboarding tagging on slide landing action
 * @param {string} currentSlide
 */
function setLandingOnboardingTagging(currentSlide) {
    Ga.send({
        hitType: 'event',
        eventCategory: 'DVF',
        eventAction: 'onboarding:popin:landed',
        eventLabel: `dvf_explorer:onbarding:popin:${currentSlide}`,
    });
}

/**
 * Update dots navigation active class
 *
 * @param {String} currentSlide current slide we are on
 */
function updateDotsNavigationActiveClass(currentSlide) {
    const navigationDots = document.querySelectorAll('[data-onboarding-navigate-dot]');

    if (navigationDots) {
        navigationDots.forEach(navigationDot => {
            const dotCorrespondingPage = navigationDot.dataset.goToStep;
            const dotIcon = navigationDot.querySelector('[data-onboarding-navigate-dot-icon]');
            if (
                currentSlide === dotCorrespondingPage &&
                !hasClass(dotIcon, 'dvf-onboarding__dot--active')
            ) {
                addClass(dotIcon, 'dvf-onboarding__dot--active');
            } else if (
                currentSlide !== dotCorrespondingPage &&
                hasClass(dotIcon, 'dvf-onboarding__dot--active')
            ) {
                removeClass(dotIcon, 'dvf-onboarding__dot--active');
            }
        });
    }
}

/**
 * Update visible onboarding step
 *
 * @param {String} nextStep next step in the onboarding
 */
function updateOnboardingVisibleStep(nextStep) {
    const onboardingSteps = document.querySelectorAll('[data-onboarding-step]');

    if (onboardingSteps) {
        onboardingSteps.forEach(step => {
            const currentStep = step.dataset.onboardingCurrentStep;

            if (currentStep === nextStep && hasClass(step, 'hide')) {
                // set tagging
                setLandingOnboardingTagging(currentStep);

                // make slide visible
                removeClass(step, 'hide');
            } else if (currentStep !== nextStep && !hasClass(step, 'hide')) {
                addClass(step, 'hide');
            }
        });
    }
}

/**
 * Set event binding inside of each tutorial page/slide
 *
 */
function navigateOnboarding() {
    const navigateBtns = document.querySelectorAll(
        '[data-onboarding-navigate-btn],[data-onboarding-navigate-dot]',
    );

    if (navigateBtns) {
        navigateBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const nextStep = btn.dataset.goToStep;
                updateOnboardingVisibleStep(nextStep);
                updateDotsNavigationActiveClass(nextStep);
            });
        });
    }

    const closeOnboardingBtn = document.querySelector('[data-onboarding-leave-btn]');

    if (closeOnboardingBtn) {
        closeOnboardingBtn.addEventListener('click', () => {
            $.magnificPopup.close();
        });
    }
}

/**
 * Start onboarding tutorial by opening popin, initializing binding and set localStorage
 */
function startOnboardingTutorial() {
    if ($('[data-onboarding-tutorial-popin]').length) {
        $.magnificPopup.open({
            items: {
                type: 'inline',
                src: '[data-onboarding-tutorial-popin]',
            },
            fixedContentPos: 'fixed',
            mainClass: 'dvf-onboarding__popin-wrapper',
            callbacks: {
                close() {
                    const steps = document.querySelectorAll('[data-onboarding-step]');

                    // set tagging only for step currently visible when user closes onboarding
                    if (steps) {
                        steps.forEach(step => {
                            if (!hasClass(step, 'hide')) {
                                const currentStep = step.dataset.onboardingCurrentStep;
                                setOnLeaveOnboardingTagging(currentStep);
                            }
                        });
                    }
                },
            },
        });

        // set tagging for first slide of onboarding
        setLandingOnboardingTagging('step1');

        // for onboarding navigation
        navigateOnboarding();

        // set local storage flag
        setOnboardingLocalStorageFlag();
    }
}

/**
 * Start onboarding tutorial only if user never done it before
 */
function initOnboardingTutorial() {
    if (!checkIfUserVisitedBefore()) {
        startOnboardingTutorial();
    }
}

/**
 *  Initialize geolocate button bindings
 */
function initGeolocateMe() {
    const buttonGeolocateMe = document.querySelector('[data-geolocate-me-button]');
    buttonGeolocateMe.addEventListener('click', () => {
        if (navigator.geolocation) {
            MA.mapboxMap.toggleLoadingState();
            navigator.geolocation.getCurrentPosition(
                position => {
                    MA.mapboxMap.toggleLoadingState();
                    const { latitude: lat, longitude: lng } = position.coords;
                    MA.mapboxMap.map.flyTo({ center: [lng, lat], zoom: ZOOM_WITHOUT_VIEW_BOX });
                },
                () => {
                    MA.mapboxMap.toggleLoadingState();

                    displayErrorToaster("Aïe! Nous n'avons pas réussi à vous géolocaliser.");
                },
                { timeout: GEOLOCATION_TIMEOUT },
            );
        } else {
            displayErrorToaster('Aïe! Vous ne semblez pas pouvoir être géolocalisé');
        }
    });
}

/**
 * Compute label for a range handling min/max value edge case
 * @param  {Array} range                 Current values of range
 * @param  {String} options.flatLabel    Label displayed when values are at both at min and max value
 * @param  {String} options.lowLabel     Label displayed when first value is at min value
 * @param  {String} options.betweenLabel Label displayed when values are not at min and max
 * @param  {String} options.highLabel    Label displayed when last value is at max value
 * @param  {String} options.numberFormat Number format displayed with units
 * @param  {String} options.appendFormat String appended to format with units
 * @param  {Number} maxValue             Maximum value
 * @param  {Number} minValue             Minimim value
 * @return {DOMElement}               Dom element containing label text
 */
function computeLabel(
    range,
    { flatLabel, lowLabel, betweenLabel, highLabel, numberFormat = '0,0', appendFormat = '' } = {},
    maxValue,
    minValue = 0,
) {
    if (!Array.isArray(range) || range.length < 2) {
        return '<span></span>';
    }

    let labelValuePrefix;
    let labelValue;

    if (range[0] === minValue && range[1] === maxValue) {
        labelValuePrefix = flatLabel;
        labelValue = '';
    } else if (range[0] === minValue) {
        labelValuePrefix = lowLabel;
        labelValue = `${numeral(range[1]).format(numberFormat)} ${appendFormat}`;
    } else if (range[1] === maxValue) {
        labelValuePrefix = highLabel;
        labelValue = `${numeral(range[0]).format(numberFormat)} ${appendFormat}`;
    } else {
        labelValuePrefix = betweenLabel;
        labelValue = `${numeral(range[0]).format('0,0')} et ${numeral(range[1]).format(
            numberFormat,
        )}${appendFormat}`;
    }

    return `<span>
        ${labelValuePrefix}
        <span class="text--bold text--primary">
            ${labelValue}
        </span>
    </span>`;
}

/**
 * Update price range label with max value handling.
 * @param  {Array}  priceRange  Minimum and maximum price value
 * @param  {Number} maxValue    Maximum available value
 */
function updatePricesLabel(priceRange, maxValue, minValue = 0) {
    const priceLabel = computeLabel(
        priceRange,
        {
            flatLabel: 'Prix indifférent',
            lowLabel: 'Prix inférieur à ',
            betweenLabel: 'Prix compris entre ',
            highLabel: 'Prix supérieur à ',
            numberFormat: '0,0 $',
            appendFormat: '',
        },
        maxValue,
        minValue,
    );
    const priceInfoMsg = document.querySelector('[data-slider-price-msg]');
    priceInfoMsg.innerHTML = priceLabel;
}

/**
 * Update area range label with max value handling.
 * @param  {Array}  priceRange  Minimum and maximum price value
 * @param  {Number} maxValue    Maximum available value
 */
function updateAreaLabel(areaRange, maxValue, minValue = 0, defaultLabel = 'Surface') {
    const areaLabel = computeLabel(
        areaRange,
        {
            flatLabel: `${defaultLabel} indifférente`,
            lowLabel: `${defaultLabel} inférieure à `,
            betweenLabel: `${defaultLabel} comprise entre `,
            highLabel: `${defaultLabel} supérieure à `,
            appendFormat: ' m²',
        },
        maxValue,
        minValue,
    );
    const areaInfoMsg = document.querySelector('[data-slider-area-msg]');
    areaInfoMsg.innerHTML = areaLabel;
}

/**
 * Update all concerned DOM element state when opening filters
 *
 * @param {Element} filterBtn
 * @param {Element} dropdown
 * @param {Element} overlay
 */
function openFilters(filterBtn, dropdown, overlay) {
    filterBtn.classList.add('dvf__filter-btn--active');
    dropdown.classList.remove('hide');
    overlay.classList.remove('hide');
}

/**
 * Compute area filter values
 * @returns {{ area_min: number, area_max: number }}
 */
function getAreaFilter() {
    const areaFilters = areaSlider.get();
    const [minArea, maxArea] = areaFilters;
    const areaFiltersValues = {};

    if (minArea && minArea !== MIN_AREA) {
        areaFiltersValues.area_min = minArea;
    }
    if (maxArea && maxArea !== MAX_AREA) {
        areaFiltersValues.area_max = maxArea;
    }

    return areaFiltersValues;
}

/**
 * Compute price filter values
 * @returns {{ price_min: number, price_max: number }}
 */
function getPriceFilter() {
    const priceFilters = priceSlider.get();
    const [minPrice, maxPrice] = priceFilters;
    const priceFiltersValues = {};

    if (minPrice && minPrice !== MIN_PRICE) {
        priceFiltersValues.price_min = minPrice;
    }
    if (maxPrice && maxPrice !== MAX_PRICE) {
        priceFiltersValues.price_max = maxPrice;
    }

    return priceFiltersValues;
}

/**
 * Compute item type filter values
 */
function getItemTypeFilter() {
    if (!filterForm) {
        return undefined;
    }

    const itemTypesValues = filterForm
        .querySelectorAll('input[name="item_types"]')
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.dataset.itemType);

    return itemTypesValues.length ? itemTypesValues.toString() : undefined;
}

/**
 * Compute room count filter values
 */
function getRoomCountFilter() {
    if (!filterForm) {
        return undefined;
    }

    const roomCountValues = filterForm
        .querySelectorAll('input[name="room_count"]')
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value);

    return roomCountValues.length ? roomCountValues.toString() : undefined;
}

/**
 * Get all filters
 * @return {object} An object containing all filters or an empty object
 */
function getFilters() {
    const filters = {
        ...getPriceFilter(),
        ...getAreaFilter(),
    };

    const itemTypeFilter = getItemTypeFilter();

    if (itemTypeFilter) {
        filters.item_types = itemTypeFilter;
    }

    const roomCountFilter = getRoomCountFilter();

    if (roomCountFilter) {
        filters.room_count = roomCountFilter;
    }
    return filters;
}

/**
 * Apply the filters (when clicked outside the dropdown or apply button
 * @param dropdown
 * @param button
 * @param overlay
 * @param priceSlider
 * @param areaSlider
 */
function applyFilters(dropdown, button, overlay) {
    if (!isMobile()) {
        addClass(dropdown, 'hide');
        removeClass(button, 'dvf__filter-btn--active');
        addClass(overlay, 'hide');
    } else {
        $.magnificPopup.close();
    }

    const filters = getFilters();

    if (Object.keys(filters).length) {
        addClass(button, 'dvf__filter-btn--filled');
    } else {
        removeClass(button, 'dvf__filter-btn--filled');
    }

    featuresMemoryCache.clear();

    toggleTransactionListLoaderState();

    MA.mapboxMap.updateFeaturesLayer(TRANSACTIONS_LAYER_ID, {
        geoJSONParams: filters,
        infoWindowParameters: { ...filters },
    });
}

/**
 * Create range slider instance
 * @param {DOMElement} wrapper DOM node that will contain slider
 * @param {String} sliderName to determine which div will contain slider data-slider-{sliderName}
 * @param {Function} updateLabel function to update label of the slider values
 * @param {Number} minValue min value
 * @param {Number} maxValue max value
 * @param {Number} defaultStep default step when manipulating slider handles
 * @param {object} range range object to define slider behavior
 * @param {object} options other options to customize slider and overwrite default behavior
 * @return {object} noUiSlider instance
 */
function createSlider(
    wrapper,
    sliderName,
    updateLabel,
    minValue,
    maxValue,
    defaultStep,
    range,
    options,
) {
    const sliderContainer = wrapper.querySelector(`[data-slider-${sliderName}]`);

    noUiSlider.create(sliderContainer, {
        start: [minValue, maxValue],
        step: defaultStep,
        connect: true,
        range,
        format: {
            to(value) {
                return parseInt(value, 10);
            },
            from(value) {
                return value;
            },
        },
        ...options,
    });

    const sliderInstance = sliderContainer.noUiSlider;

    sliderInstance.on('update', () => {
        const [minSliderValue, maxSliderValue] = sliderInstance.get();
        updateLabel([minSliderValue, maxSliderValue], maxValue);
    });

    return sliderInstance;
}

/**
 * init filters with all bindings
 */
function initFilters() {
    const overlay = document.querySelector('[data-dvf-overlay]');
    const openFiltersButton = document.querySelector('[data-dvf-filter-btn]');
    const filterFormContainer = document.querySelector(
        isMobile() ? '[data-filter-popin]' : '[data-dvf-filter-dropdown]',
    );

    filterForm = filterFormContainer.querySelector('[data-filter-form]');

    priceSlider = createSlider(
        filterFormContainer,
        'price',
        updatePricesLabel,
        MIN_PRICE,
        MAX_PRICE,
        25000,
        PRICE_SLIDER_RANGE,
    );

    areaSlider = createSlider(
        filterFormContainer,
        'area',
        updateAreaLabel,
        MIN_AREA,
        MAX_AREA,
        10,
        AREA_SLIDER_RANGE,
    );

    if (isMobile()) {
        openFiltersButton.addEventListener('click', () => {
            $.magnificPopup.open({
                fixedContentPos: 'fixed',
                items: {
                    type: 'inline',
                    src: '[data-filter-popin]',
                },
            });
        });
    } else {
        openFiltersButton.addEventListener('mousedown', e => {
            e.stopPropagation();
            if (!hasClass(openFiltersButton, 'dvf__filter-btn--active')) {
                openFilters(openFiltersButton, filterFormContainer, overlay);
            }
            return false;
        });

        filterFormContainer.addEventListener('mousedown', e => {
            e.stopPropagation();
            return false;
        });

        document.querySelector('body').addEventListener('mousedown', () => {
            if (!hasClass(filterFormContainer, 'hide')) {
                applyFilters(filterFormContainer, openFiltersButton, overlay);
            }
        });
    }

    filterForm.addEventListener('submit', e => {
        e.preventDefault();
        applyFilters(filterFormContainer, openFiltersButton, overlay);
    });

    filterForm.addEventListener('reset', () => {
        areaSlider.reset();
        priceSlider.reset();
        updateAreaLabel([MIN_AREA, MAX_AREA], MAX_AREA);
        updatePricesLabel([MIN_PRICE, MAX_PRICE], MAX_PRICE);
        removeClass(openFiltersButton, 'dvf__filter-btn--filled');
    });
}

/**
 * Triggered when dom is ready on DVF page
 */
export function domReady() {
    bindToggleMobileViewButton();
    initMap();
    initializeGeoSearchBoxes(GeoSearchBoxesSuccessCallback);
    initGeolocateMe();
    initFilters();
    initOnboardingTutorial();
    loadPastSalesReportPopover();
}
